import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaCheckDouble } from "react-icons/fa6";
import { FaInfoCircle, FaMapMarkerAlt } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import PageHeader from '../components/Shared/PageHeader';
import TextInput from "../components/Shared/TextInput";
import DropdownComponent from "../components/Shared/DropdownComponent";
import SharedButton from "../components/Shared/SharedButton";
import {
  CreateBusiness,
  editBuisness,
  // GetBusinessesList,
} from "../store/slices/BusinessSlice";
import useFetchData from "../hooks/useFetchData";

export default function CompanyModal({
  setCreateDialogVisible,
}) {
  const { t } = useTranslation();
  const { isLoading: isActionLoading } = useSelector((state) => state.business);
  const { data: tpaData, executeFetch: fetchTpas } = useFetchData();
  const [selectedTpa, setSelectedTpa] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const editData = location?.state?.editData || null;

  useEffect(() => {
    fetchTpas("POST", "getTpa");
  }, [fetchTpas]);

  const tpaList = tpaData?.data || [];

  const initialValues = {
    // ... same
    company_license: editData?.company_license || "",
    short_name: editData?.short_name || "",
    name_en: editData?.name_en || "",
    name_ar: editData?.name_ar || "",
    tpa_license_id: editData?.tpa_license_id || "",
    tpa_name_en: editData?.tpa_name_en || "",
    tpa_name_ar: editData?.tpa_name_ar || "",
    address: editData?.address || "",
    contact_info: editData?.contact_info || "",
    vat_no: editData?.vat_no || "",
    cr_no: editData?.cr_no || "",
    street_en: editData?.street_en || "",
    street_ar: editData?.street_ar || "",
    building_number: editData?.building_number || "",
    additional_number: editData?.additional_number || "",
    city_en: editData?.city_en || "",
    city_ar: editData?.city_ar || "",
    postal_code: editData?.postal_code || "",
    state_en: editData?.state_en || "",
    state_ar: editData?.state_ar || "",
    district_en: editData?.district_en || "",
    district_ar: editData?.district_ar || "",
    country_code: editData?.country_code || "SA",
    provider_code: editData?.provider_code || "",
    region: editData?.region || "Jeddah",
  };

  const validationSchema = Yup.object().shape({
    company_license: Yup.string().required(t("required")),
    name_en: Yup.string().required(t("required")),
    name_ar: Yup.string().required(t("required")),
    vat_no: Yup.string().required(t("required")),
    street_en: Yup.string().required(t("required")),
    street_ar: Yup.string().required(t("required")),
    building_number: Yup.string().required(t("required")),
    additional_number: Yup.string().required(t("required")),
    city_en: Yup.string().required(t("required")),
    city_ar: Yup.string().required(t("required")),
    postal_code: Yup.string().required(t("required")),
    state_en: Yup.string().required(t("required")),
    state_ar: Yup.string().required(t("required")),
    district_en: Yup.string().required(t("required")),
    district_ar: Yup.string().required(t("required")),
    country_code: Yup.string().required(t("required")),
    provider_code: Yup.string().required(t("required")),
    region: Yup.string().required(t("required")),
  });

  const onSubmit = async (values, { resetForm }) => {
    if (editData) {
      const resultAction = await dispatch(
        editBuisness({
          data: { ...values, tpa_id: values?.tpa },
          id: editData?.id,
        })
      );
      if (editBuisness.fulfilled.match(resultAction)) {
        resetForm();
        if (setCreateDialogVisible) {
          setCreateDialogVisible(false);
        } else {
          navigate("/manager/business");
        }
      }
    } else {
      const resultAction = await dispatch(
        CreateBusiness({
          ...values,
          tpa_id: values?.tpa,
          license_id: values?.company_license,
        })
      );
      if (CreateBusiness.fulfilled.match(resultAction)) {
        resetForm();
        if (setCreateDialogVisible) {
          setCreateDialogVisible(false);
        } else {
          navigate("/manager/business");
        }
      }
    }
  };

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)] overflow-auto">
      <PageHeader title={t(editData ? t('Update Company') : t('Create Company'))} backPath={'/manager/business'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h1 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="bg-gray-100 p-2 rounded-full">
                  <FaInfoCircle className="text-primary" size={18} />
                </span>
                {t("Basic Information")}
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full">
                <TextInput
                  label={t("name_en")}
                  name="name_en"
                  placeholder={`${t("enter")} ${t("name_en")}`}
                />

                <TextInput
                  label={t("name_ar")}
                  name="name_ar"
                  placeholder={`${t("enter")} ${t("name_ar")}`}
                />

                <TextInput
                  label={t("short_name")}
                  name="short_name"
                  placeholder={`${t("enter")} ${t("short_name")}`}
                />

                <TextInput
                  label={t("company_license")}
                  name="company_license"
                  placeholder={`${t("enter")} ${t("company_license")}`}
                />

                <TextInput
                  label={t("address")}
                  name="address"
                  placeholder={`${t("enter")} ${t("address")}`}
                />

                <TextInput
                  label={t("contact_info")}
                  name="contact_info"
                  placeholder={t("contact_info_placeholder")}
                />

                <TextInput
                  label={t("vat_no")}
                  name="vat_no"
                  placeholder={t("vat_no_placeholder")}
                />

                <TextInput
                  label={t("cr_no")}
                  name="cr_no"
                  placeholder={t("cr_no_placeholder")}
                />

                <DropdownComponent
                  name="tpa"
                  label={t("tpa")}
                  placeholder={t("select tpa")}
                  options={tpaList?.map((item) => ({
                    label: item?.name_en,
                    value: item?.id,
                  }))}
                  onChange={(e) => {
                    formik.handleChange(e);
                    const tpa = tpaList.find((tp) => tp.id === e.value);
                    setSelectedTpa(tpa);
                    formik.setFieldValue("tpa_license_id", tpa?.license_id);
                    formik.setFieldValue("tpa_name_en", tpa?.name_en);
                    formik.setFieldValue("tpa_name_ar", tpa?.name_ar);
                  }}
                />

                {selectedTpa?.license_id === "" && (
                  <TextInput
                    label={t("tpa_license_id")}
                    name="tpa_license_id"
                    placeholder={`${t("enter")} ${t("tpa_license_id")}`}
                  />
                )}
              </div>

              <h1 className="text-xl font-semibold text-gray-800 mb-4 mt-4 flex items-center gap-2">
                <span className="bg-gray-100 p-2 rounded-full">
                  <FaMapMarkerAlt className="text-primary" size={18} />
                </span>
                {t("Address Information")}
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full">
                <TextInput
                  label={t("street_en")}
                  name="street_en"
                  placeholder={`${t("enter")} ${t("street_en")}`}
                />

                <TextInput
                  label={t("street_ar")}
                  name="street_ar"
                  placeholder={`${t("enter")} ${t("street_ar")}`}
                />

                <TextInput
                  label={t("building_number")}
                  name="building_number"
                  placeholder={`${t("enter")} ${t("building_number")}`}
                />

                <TextInput
                  label={t("additional_number")}
                  name="additional_number"
                  placeholder={`${t("enter")} ${t("additional_number")}`}
                />

                <TextInput
                  label={t("city_en")}
                  name="city_en"
                  placeholder={`${t("enter")} ${t("city_en")}`}
                />

                <TextInput
                  label={t("city_ar")}
                  name="city_ar"
                  placeholder={`${t("enter")} ${t("city_ar")}`}
                />

                <TextInput
                  label={t("postal_code")}
                  name="postal_code"
                  placeholder={`${t("enter")} ${t("postal_code")}`}
                />

                <TextInput
                  label={t("state_en")}
                  name="state_en"
                  placeholder={`${t("enter")} ${t("state_en")}`}
                />

                <TextInput
                  label={t("state_ar")}
                  name="state_ar"
                  placeholder={`${t("enter")} ${t("state_ar")}`}
                />

                <TextInput
                  label={t("district_en")}
                  name="district_en"
                  placeholder={`${t("enter")} ${t("district_en")}`}
                />

                <TextInput
                  label={t("district_ar")}
                  name="district_ar"
                  placeholder={`${t("enter")} ${t("district_ar")}`}
                />

                <TextInput
                  label={t("country_code")}
                  name="country_code"
                  placeholder={`${t("enter")} ${t("country_code")}`}
                />

                <TextInput
                  label={t("provider_code")}
                  name="provider_code"
                  placeholder={`${t("enter")} ${t("provider_code")}`}
                />

                <TextInput
                  label={t("region")}
                  name="region"
                  placeholder={`${t("enter")} ${t("region")}`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <SharedButton
                type="button"
                icon="pi pi-times"
                label={t("Cancel")}
                buttonType="reset"

                onClick={() => {
                  navigate("/manager/business");
                  formik.resetForm();
                }}
              />
              <SharedButton
                type="submit"
                loading={isActionLoading}
                disabled={isActionLoading}
                icon={<FaCheckDouble size={12} className='me-2' />}
                label={editData ? t("Update") : t("Submit")}
                className="submit-btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
